import React, { useCallback, useEffect, useState } from 'react';
import type { Team } from '../../../../types';
import { teamService, type RecurringTask } from '../../services/teamService';

const DAYS = ['一', '二', '三', '四', '五', '六', '日'];

const describe = (r: RecurringTask) =>
  `每${r.weekdays.length === 7 ? '天' : '周' + r.weekdays.map(d => DAYS[d - 1]).join('')} ${String(r.hour).padStart(2, '0')}:00 后`;

/** 团队所有者/管理员：定期任务，到点自动在看板建一个待办，agent 来领 */
export const RecurringTasks: React.FC<{ team: Team }> = ({ team }) => {
  const [items, setItems] = useState<RecurringTask[]>([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [acceptance, setAcceptance] = useState('');
  const [weekdays, setWeekdays] = useState<number[]>([1]);
  const [hour, setHour] = useState(9);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setItems(await teamService.getRecurring(team.id));
    } catch {
      setError('加载定期任务失败');
    }
  }, [team.id]);

  useEffect(() => { load(); }, [load]);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || '操作失败，请重试');
    } finally {
      setBusy(false);
    }
  };

  const create = () => run(async () => {
    await teamService.createRecurring(team.id, { title, description, acceptance, weekdays, hour });
    setTitle(''); setDescription(''); setAcceptance(''); setWeekdays([1]); setHour(9); setOpen(false);
  });

  const toggleDay = (d: number) => setWeekdays(w => (w.includes(d) ? w.filter(x => x !== d) : [...w, d].sort()));
  const field = 'w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md bg-white focus:outline-none focus:border-blue-500';

  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-800">定期任务</h3>
        <span className="text-xs text-gray-400">到点自动建一个待办，agent 来领（北京时间）</span>
      </div>

      {items.length === 0 && !open && <div className="text-xs text-gray-400 mb-2">还没有定期任务，比如每周一早上的营销内容</div>}
      <ul className="space-y-2 mb-2">
        {items.map(r => (
          <li key={r.id} className="flex items-center justify-between gap-2 bg-white border border-gray-100 rounded-md px-2 py-1.5">
            <div className="min-w-0">
              <div className={`text-xs truncate ${r.enabled ? 'text-gray-800' : 'text-gray-400 line-through'}`}>{r.title}</div>
              <div className="text-xs text-gray-400">{describe(r)}{!r.acceptance && '　⚠ 没写完成标准，agent 不会领'}</div>
            </div>
            <div className="flex gap-2 shrink-0 text-xs">
              <button disabled={busy} onClick={() => run(() => teamService.updateRecurring(team.id, r.id, { enabled: !r.enabled }))} className="px-2 py-1 rounded bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50">
                {r.enabled ? '暂停' : '启用'}
              </button>
              <button disabled={busy} onClick={() => run(() => teamService.deleteRecurring(team.id, r.id))} className="px-2 py-1 rounded bg-white border border-gray-200 text-red-600 hover:bg-gray-50 disabled:opacity-50">删除</button>
            </div>
          </li>
        ))}
      </ul>

      {open ? (
        <div className="space-y-2">
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="任务标题，生成时会带上日期" maxLength={200} className={field} />
          <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="任务描述（给 agent 看的背景和要求）" rows={3} className={field} />
          <textarea value={acceptance} onChange={e => setAcceptance(e.target.value)} placeholder="完成标准（必填才会被 agent 领取）" rows={3} className={field} />
          <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-600">
            <span>每周</span>
            {DAYS.map((label, i) => (
              <button key={label} type="button" onClick={() => toggleDay(i + 1)} className={`w-6 h-6 rounded border ${weekdays.includes(i + 1) ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>{label}</button>
            ))}
            <select value={hour} onChange={e => setHour(Number(e.target.value))} className="ml-1 px-1 py-1 border border-gray-200 rounded bg-white">
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
            </select>
            <span>后生成</span>
          </div>
          <div className="flex gap-2">
            <button disabled={busy || !title.trim() || weekdays.length === 0} onClick={create} className="px-3 py-1.5 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">保存</button>
            <button onClick={() => setOpen(false)} className="px-3 py-1.5 text-xs rounded-md bg-white border border-gray-200 text-gray-600 hover:bg-gray-50">取消</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setOpen(true)} className="px-3 py-1.5 text-xs rounded-md bg-white border border-gray-200 text-gray-700 hover:bg-gray-50">新建定期任务</button>
      )}
      {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
    </div>
  );
};
